import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { uniqBy } from 'lodash'
import { useMemo, useRef } from 'react'
import { AlgoliaArticle, AlgoliaPerson } from 'types'
import { Mentionable, MentionableUser, MentionableArticle } from './Types'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import { useAuth } from 'hooks/UseAuth'

export const useSearch = ({ workspaceId }: { workspaceId: number }) => {
  const algoliaClient = useAlgoliaClient()
  const articleIndex = useMemo(() => algoliaClient.initIndex(`Article_${ALGOLIA_INDEX_SUFFIX}`), [algoliaClient])
  const peopleIndex = useMemo(() => algoliaClient.initIndex(`People_${ALGOLIA_INDEX_SUFFIX}`), [algoliaClient])
  const searchCache = useRef(new Map<string, Mentionable[]>())
  const auth = useAuth()

  const cacheSearch = (query: string, results: Mentionable[]) => {
    searchCache.current.set(query, results)
  }

  const search = async (query: string): Promise<Mentionable[]> => {
    if (!searchCache.current.has(query)) {
      const [articlesQuery, peopleQuery] = await Promise.all([
        articleIndex.search<AlgoliaArticle>({
          query,
          filters: `company:${workspaceId} OR viewable_by:${auth?.id}`,
          hitsPerPage: query ? 20 : 0,
          attributesToRetrieve: ['key', 'title', 'header_emoji'],
          restrictSearchableAttributes: ['title'],
        }),
        peopleIndex.search<AlgoliaPerson>({
          filters: `company:${workspaceId}`,
          attributesToRetrieve: ['user_id', 'profile_image_url', 'full_name'],
          restrictSearchableAttributes: ['full_name'],
          query,
          hitsPerPage: 20,
        }),
      ])

      const users: MentionableUser[] = uniqBy(peopleQuery.hits, 'user_id').map((person) => ({
        mentionableType: 'User',
        ...person,
      }))
      const articles: MentionableArticle[] = uniqBy(articlesQuery.hits, 'key').map((article) => ({
        mentionableType: 'Article',
        ...article,
      }))
      const results = [...users, ...articles]

      cacheSearch(query, results)
    }

    return searchCache.current.get(query) || []
  }

  return search
}
